import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { useFlags } from 'launchdarkly-react-client-sdk'
import { useDispatch } from 'react-redux'
import { IconButton, Tooltip } from '@mui/material'
import { ReactComponent as GeneralFeeIcon } from 'assets/GeneralFee.svg'

import { handleError } from 'utils/error'
import { useLazyGetWorkOrderDetailsQuery } from 'api/work-order/getWorkOrderDetails'
import { putIsLoading } from 'middleware/actions/response'
import { formatCentsToDollars } from 'utils/price'
import { isWORedesignFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { getWorkOrderListInvalidatesTags } from 'components/customer-details/content/work-orders/utils'

import api from 'api'
import StyledBadge from 'components/notes/common/StyledBadge'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import GeneralFeesDialog from 'components/work-order/WorkOrderReviewDialog/GeneralFeesDialog/GeneralFeesDialog'
import GeneralFeesPopover from './GeneralFeesPopover'
import { getFees } from './utils'

const GeneralFeesBadge = ({
  isLocked = false,
  totalCents = 0,
  workOrderId,
  serviceDate = '',
  configuredServiceId = '',
  onSuccess = noop,
}) => {
  const dispatch = useDispatch()
  const [viewPopoverAnchorEl, setViewPopoverAnchorEl] = useState(null)
  const [isOpenGeneralFeesDialog, setIsOpenGeneralFeesDialog] = useState(false)
  const [getWorkOrderDetails, { data: workOrder }] = useLazyGetWorkOrderDetailsQuery()
  const flags = useFlags()
  const isWORedesignEnabled = isWORedesignFeatureAvailable({ flags })

  const addedFees = useMemo(() => getFees(workOrder), [workOrder])

  const handleViewPopoverOpen = event => {
    event.stopPropagation()
    setViewPopoverAnchorEl(event.currentTarget)
  }

  const handleViewPopoverClose = () => setViewPopoverAnchorEl(null)

  const handleOpen = () => {
    dispatch(putIsLoading(true))
    getWorkOrderDetails({ workOrderId })
      .unwrap()
      .then(() => setIsOpenGeneralFeesDialog(true))
      .catch(handleError)
      .finally(() => dispatch(putIsLoading(false)))
  }

  const handleClose = () => setIsOpenGeneralFeesDialog(false)

  const handleRefresh = () => {
    const woInvalidateTags = getWorkOrderListInvalidatesTags({ isNew: isWORedesignEnabled, serviceDate, csId: configuredServiceId })
    dispatch(api.util.invalidateTags([...woInvalidateTags]))
    onSuccess()
  }

  const total = formatCentsToDollars(totalCents)
  // Support for negative values
  const hasTotal = totalCents !== 0
  const hasDecimal = !Number.isInteger(totalCents)

  return (
    <>
      <Tooltip arrow placement="bottom" title="General fees" disableHoverListener={hasTotal} disableInteractive>
        <IconButton
          size="small"
          onClick={handleOpen}
          {...(hasTotal && {
            onMouseEnter: handleViewPopoverOpen,
            onMouseLeave: handleViewPopoverClose,
          })}
        >
          <StyledBadge
            badgeContent={hasTotal ? <HHDisplayMoney fixedDecimalScale={hasDecimal} value={total} formatToDollars={false} /> : 0}
          >
            <GeneralFeeIcon />
          </StyledBadge>
        </IconButton>
      </Tooltip>
      <GeneralFeesPopover anchorEl={viewPopoverAnchorEl} workOrderId={workOrderId} onClose={handleViewPopoverClose} />
      <GeneralFeesDialog
        isLocked={isLocked}
        open={isOpenGeneralFeesDialog}
        woId={workOrderId}
        onRefresh={handleRefresh}
        onClose={handleClose}
        fees={addedFees}
      />
    </>
  )
}

GeneralFeesBadge.propTypes = {
  isLocked: PropTypes.bool,
  totalCents: PropTypes.array,
  workOrderId: PropTypes.string.isRequired,
  serviceDate: PropTypes.string,
  configuredServiceId: PropTypes.string,
  onSuccess: PropTypes.func,
}

export default GeneralFeesBadge
